import { ORG_SCOPED_TABLES, quoteIdentifier } from '../schema';

import type { OfflineDbExecutor } from '../driver-types';

/**
 * Per-scope sync bookkeeping: prefetch throttling, purge decisions, and the
 * offline "last updated N ago" affordance. `accountId` is '' for org-scoped
 * families (never NULL - SQLite treats composite-PK NULLs as distinct, which
 * would let duplicate rows accumulate).
 *
 * `cursor` is load-bearing in v1, whatever a delta-sync design might later
 * want it for: it holds the VIEW SIGNATURE of the rows a family currently
 * contains, and an offline read serves those rows only while it matches the
 * view being asked for. Hydration writes clear it to NULL first, so any
 * mid-write failure reads as a cache MISS rather than as a matching signature
 * over a half-purged family. Repurposing or nulling this column turns every
 * offline read into a hit on the wrong view.
 */
export async function getSyncState(
  driver: OfflineDbExecutor,
  organizationId: string,
  scope: string,
  accountId = '',
): Promise<{ lastSyncedAt: number; cursor: string | null } | null> {
  const rows = await driver.query<{ last_synced_at: number; cursor: string | null }>(
    `SELECT last_synced_at, cursor FROM sync_state
     WHERE organization_id = ? AND account_id = ? AND scope = ?`,
    [organizationId, accountId, scope],
  );
  if (rows.length === 0) {
    return null;
  }
  return { lastSyncedAt: rows[0].last_synced_at, cursor: rows[0].cursor };
}

export async function setSyncState(
  driver: OfflineDbExecutor,
  organizationId: string,
  scope: string,
  lastSyncedAt: number,
  accountId = '',
  cursor: string | null = null,
): Promise<void> {
  await driver.run(
    `INSERT INTO sync_state (organization_id, account_id, scope, last_synced_at, cursor)
     VALUES (?, ?, ?, ?, ?)
     ON CONFLICT(organization_id, account_id, scope) DO UPDATE SET
       last_synced_at = excluded.last_synced_at,
       cursor = excluded.cursor`,
    [organizationId, accountId, scope, lastSyncedAt, cursor],
  );
}

/**
 * Remove every row an organization owns, across all families. The purge for
 * a gate flip to OFF or a membership revocation, applied at the next online
 * evaluation (offline devices cannot receive realtime purge signals - the
 * R15 read lock bounds how long they can keep reading instead).
 */
export async function purgeOrganization(
  driver: OfflineDbExecutor,
  organizationId: string,
): Promise<void> {
  // The schema exports the org-scoped list so a new table added there is
  // automatically covered here - a hand-typed copy would silently drift and
  // leave one family behind on a revocation purge.
  for (const table of ORG_SCOPED_TABLES) {
    await driver.run(`DELETE FROM ${table} WHERE organization_id = ?`, [organizationId]);
  }
}

/**
 * Every organisation this device currently holds rows for.
 *
 * The sync worker needs it to answer a question no single-org pass can: which
 * cached orgs the signed-in user is no longer an active member of. Without it
 * a multi-org user revoked from an org they are not currently in keeps that
 * org's rows readable offline indefinitely, because the R15 stamp is
 * device-wide and passes for the org they ARE in keep refreshing it.
 *
 * Every org-scoped table, not just `sync_state`: the email-body payload family
 * writes rows without any sync bookkeeping of its own, so a `sync_state`-only
 * scan would miss an org whose only residue is cached message bodies. Reads
 * ids only, so the R15 payload gate leaves it alone while locked - which is
 * required, since a locked device must still be able to purge.
 */
export async function listCachedOrganizationIds(driver: OfflineDbExecutor): Promise<string[]> {
  // No per-branch DISTINCT: `UNION` (not `UNION ALL`) already de-duplicates
  // across the whole compound query, so a DISTINCT per table is a second sort
  // of the same rows. Nulls and blanks are excluded in SQL rather than after
  // the round-trip - this scans every org-scoped table, so the rows not worth
  // returning are the ones not worth serialising either.
  const statement = ORG_SCOPED_TABLES.map(
    (table) =>
      `SELECT organization_id FROM ${quoteIdentifier(table)} WHERE organization_id IS NOT NULL AND organization_id <> ''`,
  ).join(' UNION ');
  const rows = await driver.query<{ organization_id: string | null }>(statement, []);
  return rows
    .map((row) => row.organization_id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}
